interface CrossAppCacheEntry {
  value: unknown;
  ttl?: number;
  timestamp: number;
  cacheOnlyOnce?: boolean;
  isAsync?: boolean;
}

declare global {
  interface Window {
    __hydra_cross_app_cache__?: {
      cache: Record<string, CrossAppCacheEntry>;
      setValue: ({
        key,
        value,
        ttl,
        cacheOnlyOnce,
        isAsync,
      }: {
        key: string;
        value: unknown;
        ttl?: number;
        cacheOnlyOnce?: boolean;
        isAsync?: boolean;
      }) => void;
      getValue: (key: string) => unknown;
      getValueAsync: (key: string) => Promise<unknown>;
    };
  }
}

/** Simple cross-app cache for storing request data in the browser's global state.
 * Intended to prevent duplicate requests to the same endpoint in different apps,
 * particularly during the initial load of the app.
 *
 * Could probably be a separate package in the future, but it's simple enough
 * to keep it here for now.
 */
export function initializeCrossAppCache() {
  if (window.__hydra_cross_app_cache__ != null) {
    return window.__hydra_cross_app_cache__;
  }

  window.__hydra_cross_app_cache__ = {
    cache: {},
    setValue: ({
      key,
      value,
      ttl,
      cacheOnlyOnce,
      isAsync,
    }: {
      key: string;
      value: unknown;
      ttl?: number;
      cacheOnlyOnce?: boolean;
      isAsync?: boolean;
    }) => {
      setCrossAppCache({ key, value, ttl, cacheOnlyOnce, isAsync });
    },
    getValue: (key: string) => {
      return getCrossAppCache(key);
    },
    getValueAsync: (key: string) => {
      return getCrossAppCacheAsync(key);
    },
  };

  return window.__hydra_cross_app_cache__;
}

export const crossAppCache = initializeCrossAppCache();

/** Caches a value for use within Hydra or in other app bundles.
 *
 * @param key - The key to cache the value under.
 * @param value - The value to cache.
 * @param ttl - The time to live for the cached value, in milliseconds.
 * @param cacheOnlyOnce - If true, the value will only be cached once, and never overwritten.
 * To be used in conjunction with `ttl` for caching requests during the initial load of the app, where
 * the value is expected to be the same for a short period of time, but might be updated later.
 */
export function setCrossAppCache({
  key,
  value,
  ttl,
  cacheOnlyOnce = false,
  isAsync = false,
}: {
  key: string;
  value: unknown;
  ttl?: number;
  cacheOnlyOnce?: boolean;
  isAsync?: boolean;
}) {
  if (window.__hydra_cross_app_cache__ == null) {
    return;
  }

  if (cacheOnlyOnce === true && window.__hydra_cross_app_cache__.cache[key] != null) {
    return;
  }

  window.__hydra_cross_app_cache__.cache[key] = {
    value,
    ttl,
    timestamp: Date.now(),
    cacheOnlyOnce,
    isAsync,
  };
}

function internalGetCrossAppCache(key: string, { allowAsync = false }: { allowAsync?: boolean } = {}) {
  if (window.__hydra_cross_app_cache__ == null) {
    return null;
  }
  const cachedValue = window.__hydra_cross_app_cache__.cache[key];
  if (cachedValue == null) {
    return null;
  }
  if (cachedValue.ttl != null && cachedValue.timestamp + cachedValue.ttl < Date.now()) {
    return null;
  }

  if (cachedValue.isAsync === true && !allowAsync) {
    return null;
  }

  return cachedValue.value;
}

export function getCrossAppCache(key: string) {
  return internalGetCrossAppCache(key, { allowAsync: false });
}

export async function getCrossAppCacheAsync(key: string) {
  return await internalGetCrossAppCache(key, { allowAsync: true });
}

/**
 * Drop specific cached entries by exact key.
 *
 * Prefer this over `deleteCrossAppCacheByPrefix` when the keys you want are
 * nested under a prefix other endpoints also use. `/accounts` in particular is
 * shared: besides the account payloads it covers
 * `/accounts/{id}/billing/{profile,info,usage}` — cached specifically to keep
 * repeat boots from tripping the billing vendor's rate limits — and
 * `/accounts/{id}/role_definitions`. A prefix bust takes those out as
 * collateral, which costs nothing in correctness (every reader refetches on a
 * miss) but throws away protection that was added on purpose.
 */
export function deleteCrossAppCacheKeys(...keys: string[]) {
  const store = window.__hydra_cross_app_cache__;
  if (store == null) {
    return;
  }
  for (const key of keys) {
    delete store.cache[key];
  }
}

/**
 * Drop every cached entry whose key starts with `prefix`. Used to invalidate a
 * family of endpoints (e.g. the account payloads) after a mutation so the next
 * read misses the cache and refetches fresh data instead of a stale TTL value.
 */
export function deleteCrossAppCacheByPrefix(prefix: string) {
  const store = window.__hydra_cross_app_cache__;
  if (store == null) {
    return;
  }
  for (const key of Object.keys(store.cache)) {
    if (key.startsWith(prefix)) {
      delete store.cache[key];
    }
  }
}
